import React, { useState, useCallback, useEffect } from "react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Settings, LogOut, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { BrandingSection } from "@/components/admin/BrandingSection";
import { SEOSection } from "@/components/admin/SEOSection";
import { ColorsSection } from "@/components/admin/ColorsSection";
import { ROISettingsSection } from "@/components/admin/ROISettingsSection";
import { TrackingSection } from "@/components/admin/TrackingSection";
import { LoomySection } from "@/components/admin/LoomySection";
import { LeadsAnalyticsSection } from "@/components/admin/LeadsAnalyticsSection";
import { DiagnosticsAdminSection } from "@/components/admin/DiagnosticsAdminSection";
import { BenchmarksSection } from "@/components/admin/BenchmarksSection";
import { ScoringAdminSection } from "@/components/admin/ScoringAdminSection";
import { KPIDashboard } from "@/components/admin/KPIDashboard";
import { CampaignTracker } from "@/components/admin/CampaignTracker";
import { ABTestSection } from "@/components/admin/ABTestSection";
import { SecurityLogsSection } from "@/components/admin/SecurityLogsSection";
import { TwoFactorSection } from "@/components/admin/TwoFactorSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { logout } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    logo_light: "",
    logo_dark: "",
    favicon: "",
    og_image: "",
    meta_title: "Lead Artisan - Solution IA pour Plombiers 24/7",
    meta_description: "Ne perdez plus jamais une urgence rentable.",
    color_primary: "212 85% 35%",
    color_secondary: "205 45% 55%",
    color_accent: "25 95% 53%",
    solution_price: 350,
    facebook_pixel_id: "",
    google_analytics_id: "",
    google_tag_manager_id: "",
    loomy_brand_api_key: "",
    whatsapp_number: "",
    loomy_rdv_url: "",
    webhook_url: "",
  });

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/settings/`);
      const data = response.data;
      // Coalesce null values to empty strings for controlled inputs
      setSettings((prev) => {
        const merged = { ...prev };
        for (const key of Object.keys(merged)) {
          if (key in data) merged[key] = data[key] ?? prev[key];
        }
        return merged;
      });
    } catch {
      toast.error("Erreur lors du chargement des parametres");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleInputChange = useCallback((field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFileUpload = useCallback(async (file, fileType) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);
    try {
      const response = await axios.post(`${API}/settings/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      handleInputChange(fileType, fullUrl);
      toast.success("Image uploadee avec succes");
    } catch {
      toast.error("Erreur lors de l'upload");
    }
  }, [handleInputChange]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/settings/`, settings);
      toast.success("Parametres sauvegardes avec succes");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Back-Office Lead Artisan</h1>
              <p className="text-sm text-muted-foreground">Gestion des parametres du site</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Deconnexion
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="space-y-6">
          <KPIDashboard />
          <CampaignTracker />
          <LeadsAnalyticsSection />
          <DiagnosticsAdminSection />
          <BenchmarksSection />
          <ScoringAdminSection />
          <ABTestSection />
          <SecurityLogsSection />
          <TwoFactorSection />
          <BrandingSection settings={settings} onInputChange={handleInputChange} onFileUpload={handleFileUpload} />
          <SEOSection settings={settings} onInputChange={handleInputChange} />
          <ColorsSection settings={settings} onInputChange={handleInputChange} />
          <ROISettingsSection settings={settings} onInputChange={handleInputChange} />
          <TrackingSection settings={settings} onInputChange={handleInputChange} />
          <LoomySection settings={settings} onInputChange={handleInputChange} />

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={loadSettings}>Annuler</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-accent hover:bg-accent-hover">
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
